/**
 * Warehouse Reports (docs/designs/rsw-warehouse-reports.md): the RSW reports, one endpoint, with
 * the shape, headers and totals defined once in `@nexseed/shared` (`WAREHOUSE_REPORTS`). The ones
 * the CSW tab has too take the CSW switches (With SLOC / dates, WH-Bin-Sub-Bin, QC age).
 */
import {
  PACKING_VIEWS,
  PACK_AGE_BANDS,
  PACK_AGE_COMPARE,
  PACK_QC_BASES,
  type PackAgeBand,
  type PackAgeCompare,
  type PackQcBasis,
  type PackingView,
  type ReportResult,
  type WarehouseReportKey,
} from "@nexseed/shared";
import { keepPreviousData, useQuery } from "@tanstack/react-query";
import { z } from "zod";
import { downloadExport, withQuery } from "@/features/inventory/export";
import { api } from "@/lib/api-client";
import {
  DATE_MODES,
  resolveDateFilter,
  todayInIndia,
  type DateMode,
} from "@/lib/dates";
import { openPdf } from "@/lib/open-pdf";
import type { WarehouseStage } from "../api";

const id = z.coerce.number().int().positive().optional().catch(undefined);
const yesNo = z.enum(["yes", "no"]).optional().catch(undefined);

/** The filters every warehouse report may take (RSW and CSW). */
export const reportSearch = z.object({
  cropId: id,
  variety: z.string().optional().catch(undefined),
  date: z.iso.date().optional().catch(undefined),
  /** Period reports: All dates (none) / Today / Specific date / Date range. */
  dates: z.enum(DATE_MODES).optional().catch(undefined),
  from: z.iso.date().optional().catch(undefined),
  to: z.iso.date().optional().catch(undefined),
  withSloc: yesNo,
  withDates: yesNo,
  whId: id,
  binId: id,
  subBinId: id,
  minDays: z.coerce.number().int().min(0).optional().catch(undefined),
  minAgeDays: z.coerce.number().int().min(0).optional().catch(undefined),
  /** Packaging reports (packaging-reports.ts). */
  packSizeId: id,
  packQc: z.enum(PACK_QC_BASES).optional().catch(undefined),
  ageBand: z
    .enum(Object.keys(PACK_AGE_BANDS) as [PackAgeBand, ...PackAgeBand[]])
    .optional()
    .catch(undefined),
  ageCmp: z
    .enum(
      Object.keys(PACK_AGE_COMPARE) as [PackAgeCompare, ...PackAgeCompare[]]
    )
    .optional()
    .catch(undefined),
  ageDays: z.coerce.number().int().min(0).max(3650).optional().catch(undefined),
  view: z.enum(PACKING_VIEWS).optional().catch(undefined),
  withReprint: yesNo,
  /** Stock reports (stock-reports.ts): the As on day, and the vertical ("all" or its id). */
  asOn: z.iso.date().optional().catch(undefined),
  vertical: z
    .string()
    .regex(/^(all|[1-9]\d{0,9})$/)
    .optional()
    .catch(undefined),
  /** A report's own pickers (`ReportDef.choices`): one of the option values. */
  kind: z
    .string()
    .regex(/^[a-z_]{1,24}$/)
    .optional()
    .catch(undefined),
  mode: z
    .string()
    .regex(/^[a-z_]{1,24}$/)
    .optional()
    .catch(undefined),
  seedStage: z
    .string()
    .regex(/^[a-z_]{1,24}$/)
    .optional()
    .catch(undefined),
  /** Periodical Dispatch: the destination plant. */
  plantId: id,
  /** Stock reports: a classification id, or "none". */
  classification: z
    .string()
    .regex(/^(none|[1-9]\d{0,9})$/)
    .optional()
    .catch(undefined),
});

export interface ReportSearch {
  cropId?: number | undefined;
  /** A variety id, or "coded" / "unidentified". */
  variety?: string | undefined;
  date?: string | undefined;
  dates?: DateMode | undefined;
  from?: string | undefined;
  to?: string | undefined;
  /** Crop Variety wise: the legacy "With SLOC" (default yes) and "With Harvest & Arrival Date". */
  withSloc?: "yes" | "no" | undefined;
  withDates?: "yes" | "no" | undefined;
  /** Warehouse wise. */
  whId?: number | undefined;
  binId?: number | undefined;
  subBinId?: number | undefined;
  /** QC Ageing: more than this many days since the last QC test. */
  minDays?: number | undefined;
  /** RSW Coded: more than this many days since arrival (legacy "Age"). */
  minAgeDays?: number | undefined;
  /** Packaging: the UPS (pack size) and the "Pack QC Status" basis. */
  packSizeId?: number | undefined;
  packQc?: PackQcBasis | undefined;
  /** Packaging QC Ageing: a duration band, or a comparison with a number of days. */
  ageBand?: PackAgeBand | undefined;
  ageCmp?: PackAgeCompare | undefined;
  ageDays?: number | undefined;
  /** Periodical Packing: Summary or Detail. */
  view?: PackingView | undefined;
  /** Periodical Packing's "With Re-Printing" tick. */
  withReprint?: "yes" | "no" | undefined;
  /** Stock reports: the day stock is counted on (today when unset). */
  asOn?: string | undefined;
  /** Stock reports: a vertical id, or "all". */
  vertical?: string | undefined;
  /** A report's own pickers (`ReportDef.choices`). */
  kind?: string | undefined;
  mode?: string | undefined;
  seedStage?: string | undefined;
  /** Periodical Dispatch: the destination plant. */
  plantId?: number | undefined;
  /** Stock reports: a classification id, or "none" (no classification). */
  classification?: string | undefined;
}

export interface ReportQuery extends ReportSearch {
  stage: WarehouseStage;
  facilityId?: number | undefined;
}

const path = (key: WarehouseReportKey) => `/warehouse/reports/${key}`;

/** The From/To a period report runs on: Today is worked out now, not kept in the link. */
export const reportPeriod = (s: ReportSearch) =>
  resolveDateFilter(s.dates, s.from, s.to, todayInIndia());

const params = (q: ReportQuery) => ({
  stage: q.stage,
  facilityId: q.facilityId,
  cropId: q.cropId,
  variety: q.variety,
  date: q.date,
  ...reportPeriod(q),
  withSloc: q.withSloc,
  withDates: q.withDates,
  whId: q.whId,
  binId: q.binId,
  subBinId: q.subBinId,
  minDays: q.minDays,
  minAgeDays: q.minAgeDays,
});

export function useWarehouseReport(
  key: WarehouseReportKey,
  q: ReportQuery,
  enabled = true
) {
  return useQuery({
    queryKey: ["warehouse", "report", key, params(q)],
    queryFn: () => api<ReportResult>(withQuery(path(key), params(q))),
    enabled,
    placeholderData: keepPreviousData,
  });
}

export const reportPdf = (key: WarehouseReportKey, q: ReportQuery) =>
  openPdf(withQuery(path(key), { ...params(q), format: "pdf" }));

/** The Excel workbook, saved under the name the server gives. */
export const reportXlsx = (key: WarehouseReportKey, q: ReportQuery) =>
  downloadExport(path(key), { ...params(q), format: "xlsx" }, `${key}.xlsx`);
